import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, FolderOpen, ChevronRight, X, AlertCircle, Package } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../../components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    image: '',
    parent_id: '',
    description: '',
    is_active: true,
    display_order: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      // Fallback to public endpoint
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data || []);
      } catch (e) {
        toast.error('Failed to load categories');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const openAddModal = (parentId = null) => {
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      image: '',
      parent_id: parentId || '',
      description: '',
      is_active: true,
      display_order: 0
    });
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      slug: category.slug || '',
      image: category.image || '',
      parent_id: category.parent_id || '',
      description: category.description || '',
      is_active: category.is_active !== false,
      display_order: category.display_order || 0
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Category name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const data = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name)
      };

      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/admin/categories`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Category created');
      }
      
      setShowModal(false);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoryId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category deleted');
      setShowDeleteConfirm(null);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const toggleExpand = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-categories-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light text-charcoal">Categories</h1>
          <p className="text-charcoal-light text-sm mt-1">
            Organize your products with categories and subcategories
          </p>
        </div>
        <button
          onClick={() => openAddModal()}
          className="inline-flex items-center gap-2 bg-charcoal text-ivory px-6 py-3 text-xs tracking-wider uppercase font-medium hover:bg-charcoal/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Category
        </button>
      </div>

      {/* Categories List */}
      {categories.length === 0 ? (
        <div className="bg-white border border-charcoal/10 p-12 text-center">
          <FolderOpen className="w-12 h-12 mx-auto mb-4 text-charcoal-light" />
          <h3 className="font-heading text-xl text-charcoal mb-2">No Categories Yet</h3>
          <p className="text-charcoal-light mb-6">Create your first category to organize products.</p>
          <button
            onClick={() => openAddModal()}
            className="inline-flex items-center gap-2 bg-gold text-white px-6 py-3 text-xs tracking-wider uppercase font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </button>
        </div>
      ) : (
        <div className="bg-white border border-charcoal/10">
          {categories.map(category => (
            <div key={category.id} className="border-b border-charcoal/5 last:border-b-0">
              {/* Parent Category */}
              <div className="flex items-center gap-4 p-4 hover:bg-beige/30 transition-colors">
                <button
                  onClick={() => toggleExpand(category.id)}
                  className={`p-1 ${category.subcategories?.length > 0 ? 'text-charcoal' : 'text-transparent'}`}
                  disabled={!category.subcategories?.length}
                >
                  <ChevronRight className={`w-4 h-4 transition-transform ${
                    expandedCategories.includes(category.id) ? 'rotate-90' : ''
                  }`} />
                </button>
                
                {category.image ? (
                  <img src={category.image} alt={category.name} className="w-12 h-12 object-cover" />
                ) : (
                  <div className="w-12 h-12 bg-beige flex items-center justify-center">
                    <FolderOpen className="w-6 h-6 text-charcoal-light" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-charcoal">{category.name}</h3>
                    {category.is_active === false && (
                      <span className="px-2 py-0.5 bg-charcoal-light/20 text-charcoal-light text-xs">Inactive</span>
                    )}
                  </div>
                  <p className="text-xs text-charcoal-light">
                    {category.product_count || 0} products • {category.subcategories?.length || 0} subcategories
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openAddModal(category.id)}
                    className="px-3 py-1.5 text-xs text-charcoal border border-charcoal/20 hover:bg-beige"
                  >
                    + Subcategory
                  </button>
                  <button
                    onClick={() => openEditModal(category)}
                    className="p-2 text-charcoal-light hover:text-charcoal hover:bg-beige"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(category.id)}
                    className="p-2 text-charcoal-light hover:text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Subcategories */}
              {expandedCategories.includes(category.id) && category.subcategories?.length > 0 && (
                <div className="bg-beige/20 border-t border-charcoal/5">
                  {category.subcategories.map(sub => (
                    <div key={sub.id} className="flex items-center gap-4 p-4 pl-16 hover:bg-beige/50 transition-colors">
                      {sub.image ? (
                        <img src={sub.image} alt={sub.name} className="w-10 h-10 object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-beige flex items-center justify-center">
                          <Package className="w-5 h-5 text-charcoal-light" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm text-charcoal">{sub.name}</h4>
                          {sub.is_active === false && (
                            <span className="px-2 py-0.5 bg-charcoal-light/20 text-charcoal-light text-xs">Inactive</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEditModal(sub)}
                          className="p-1.5 text-charcoal-light hover:text-charcoal hover:bg-beige"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(sub.id)}
                          className="p-1.5 text-charcoal-light hover:text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4">
          <div className="bg-ivory max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="font-heading text-xl text-charcoal">Confirm Delete</h3>
            </div>
            <p className="text-charcoal-light mb-6">
              Are you sure you want to delete this category? Categories with products or subcategories cannot be deleted.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-6 py-2.5 border border-charcoal/20 text-charcoal text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-6 py-2.5 bg-red-600 text-white text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4">
          <div className="bg-ivory max-w-lg w-full">
            <div className="px-6 py-4 border-b border-charcoal/10 flex items-center justify-between">
              <h2 className="font-heading text-xl text-charcoal">
                {editingCategory ? 'Edit Category' : formData.parent_id ? 'Add Subcategory' : 'Add Category'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-beige">
                <X className="w-5 h-5 text-charcoal" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                  placeholder="e.g., T-Shirts"
                />
              </div>
              
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                  Slug (URL)
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                  placeholder="t-shirts"
                />
              </div>
              
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold resize-none"
                  placeholder="Category description..."
                />
              </div>
              
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                  Category Image
                </label>
                {formData.image ? (
                  <div className="relative w-32 h-32">
                    <img src={formData.image} alt="Category" className="w-full h-full object-cover border border-charcoal/10" />
                    <button
                      onClick={() => setFormData({ ...formData, image: '' })}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <ImageUpload 
                    onUploadSuccess={(url) => setFormData({ ...formData, image: url })}
                    folder="categories"
                  />
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                    Status
                  </label>
                  <label className="flex items-center gap-3 px-4 py-3 border border-charcoal/20 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 accent-gold"
                    />
                    <span className="text-sm text-charcoal">Active</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-charcoal/10 flex justify-end gap-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2.5 border border-charcoal/20 text-charcoal text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 bg-charcoal text-ivory text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
